/**
 * Cohort display helpers, shared by the checkout selector and the homepage
 * schedule.
 *
 * They live here rather than in either component because the two must agree.
 * The seat badge in particular carries a promise — "Aproape plin · 3 locuri"
 * on the homepage and "13 locuri disponibile" in checkout are read minutes
 * apart by the same person, and a threshold that drifted between two copies
 * would show them a cohort filling up in one place and roomy in the other.
 *
 * Pure functions, no React: they are the part of the cohort UI that can be
 * tested without a DOM.
 */

export const FORMAT_LABEL = { online: "Online", fizic: "Fizic", hibrid: "Hibrid" };

/** Seat count below which the badge switches from calm green to amber. */
export const ALMOST_FULL_THRESHOLD = 3;

const MONTH_ABBR = [
  "ian", "feb", "mar", "apr", "mai", "iun",
  "iul", "aug", "sep", "oct", "noi", "dec",
];

/** Cohort dates are plain "YYYY-MM-DD". `new Date` reads a bare date string as
 *  UTC midnight, which lands on the previous day for anyone west of Greenwich —
 *  a cohort starting on the 1st would read "31" to a visitor abroad. Anchoring
 *  at midday puts every timezone we plausibly serve on the intended date. */
function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatStartDate(iso) {
  const d = parseDate(iso);
  if (!d) return iso || "—";
  return d.toLocaleDateString("ro-RO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function periodLabel(start, end) {
  const s = parseDate(start);
  if (!s) return start || "—";
  const opts = { day: "numeric", month: "short" };
  const from = s.toLocaleDateString("ro-RO", opts);
  const e = parseDate(end);
  if (!e) return `de la ${from}`;
  return `${from} – ${e.toLocaleDateString("ro-RO", { ...opts, year: "numeric" })}`;
}

/** The day/month pair for the calendar block that opens each row. Returns null
 *  for an unusable date so the caller can drop the block rather than print a
 *  placeholder where a number belongs. */
export function startDayMonth(iso) {
  const d = parseDate(iso);
  if (!d) return null;
  return { day: String(d.getDate()), month: MONTH_ABBR[d.getMonth()] };
}

/**
 * Whether a cohort can be joined at all.
 *
 * Read directly rather than through the badge below, because the badge is now
 * allowed to be absent — a cohort that publishes no counts and is neither
 * nearly full nor full shows nothing. Fullness must survive that.
 */
export function isFull(g) {
  return Boolean(g?.is_full || g?.status === "full");
}

/**
 * What the public site says about availability, or `null` for "say nothing".
 *
 * Three shapes, and which one appears depends on the cohort's own `show_seats`
 * switch, off by default:
 *
 *   full            → "Grupă completă"    — always, switch or no switch
 *   nearly full     → "Aproape plin"      — with a count only when published
 *   room to spare   → a count, or nothing
 *
 * The last line is the point of the switch. A group that has just opened reads
 * "1/20 înscriși", which tells a visitor nobody is coming rather than that
 * there is room — so by default it says nothing at all, and the operator turns
 * counts on for a cohort whose numbers are worth showing.
 *
 * `almost_full` is computed on the server and arrives even when the counts do
 * not: the threshold cannot be applied to a number that was never sent.
 */
export function statusBadge(g) {
  if (isFull(g)) {
    return { label: "Grupă completă", cls: "bg-[#E63946]/10 text-[#9b1f2c]", disabled: true };
  }

  const seats = typeof g?.spots_left === "number" ? g.spots_left : null;
  const nearlyFull = seats !== null ? seats <= ALMOST_FULL_THRESHOLD : Boolean(g?.almost_full);

  if (nearlyFull) {
    return {
      label: seats !== null
        ? `Aproape plin · ${seats} loc${seats === 1 ? "" : "uri"}`
        : "Aproape plin",
      cls: "bg-[#F59E0B]/15 text-[#92400E]",
      disabled: false,
    };
  }

  if (seats === null) return null;   // counts withheld, and nothing to hurry about

  return {
    label: `${seats} locuri disponibile`,
    cls: "bg-[#519872]/15 text-[#2F6B4E]",
    disabled: false,
  };
}

/** "Luni, Miercuri · 18:00 - 20:00" — empty parts are dropped, not padded. */
export function scheduleLabel(g) {
  const days = (g.schedule_days || []).join(", ");
  const time = (g.schedule_time || "").trim();
  if (days && time) return `${days} · ${time}`;
  return days || time || "—";
}

/**
 * The cohort a `?grupa=<id>` link should select, or null.
 *
 * Null covers three different disappointments that all end the same way — the
 * id names no cohort, names one that has since closed, or names a full one —
 * and the caller clears the selection rather than leaving checkout looking
 * decided while submitting nothing.
 *
 * A function rather than three lines inside an effect because those three
 * lines crashed in production: they read `statusBadge(match).disabled`, and
 * the badge is allowed to be null for a cohort that publishes no seat counts,
 * which is now every cohort by default. `isFull` exists precisely so fullness
 * never has to be read through the badge; here it is used, and here it can be
 * tested without a DOM.
 */
export function preselectedGroup(groups, wantedId) {
  if (!Array.isArray(groups) || !wantedId) return null;
  const match = groups.find((g) => g?.id === wantedId);
  return match && !isFull(match) ? match : null;
}

/** True when every cohort in the list is full — the homepage swaps its CTA. */
export function allGroupsFull(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return false;
  return groups.every(isFull);
}

const MONTH_NAME = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

/**
 * The months the open cohorts start in, in order, each with how many start
 * then (23.09.2026). Used by the homepage filter, so it is built from the
 * cohorts on screen: a month nobody starts in never becomes a button.
 *
 * The year is part of the value (`2026-10`) and part of the label only when
 * the cohorts span more than one year — "Octombrie" is unambiguous until
 * there is an October in two different years.
 */
export function monthFilters(groups) {
  const counts = new Map();
  (groups || []).forEach((g) => {
    const d = parseDate(g?.start_date);
    if (!d) return;
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const item = counts.get(value) || { value, year: d.getFullYear(), month: d.getMonth(), count: 0 };
    item.count += 1;
    counts.set(value, item);
  });
  const rows = [...counts.values()].sort((a, b) => a.value.localeCompare(b.value));
  const manyYears = new Set(rows.map((r) => r.year)).size > 1;
  return rows.map((r) => ({
    value: r.value,
    count: r.count,
    label: manyYears ? `${MONTH_NAME[r.month]} ${r.year}` : MONTH_NAME[r.month],
  }));
}

/** Does this cohort start in `2026-10`? An empty filter means "all of them". */
export function startsInMonth(group, value) {
  if (!value) return true;
  const d = parseDate(group?.start_date);
  if (!d) return false;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === value;
}

/**
 * Where the chosen cohort sits in the list on screen, or -1 when it is not
 * there (filtered out, full, gone). The selector uses it to bring that card
 * into view: arriving from the homepage with a cohort already chosen and
 * having to press an arrow twice to see it is how people lose track of what
 * they picked (23.09.2026).
 */
export function selectedIndex(groups, value) {
  if (!value) return -1;
  return (groups || []).findIndex((g) => g?.id === value);
}
